// Path: src/components/permissions/PermissionTable.tsx
// Author: ISTP_Dev
// Description: Dual-level permission table with Batch Delete Logic for Schools

import React, { useState, useEffect, useMemo } from 'react';
import { 
  type School, 
  type PermissionType, 
  getCourseUserPermissions,
  getStageUserPermissions,
  getLessonUserPermissions,
  deleteCourseUserPermissions,
  deleteStageUserPermissions,
  deleteLessonUserPermissions,
  type SchoolPermissionSummary,
  type UserPermissionInfo,
  type SchoolWithUsersPermission
} from '../../lib/api';
import * as api from '../../lib/api';
import { type ResourceContext } from '../../types/permissionTypes';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  selectedNode: ResourceContext | null;
  drillDownSchool: School | null;
  onDrillDown: (school: School | null) => void;
  onAdd: () => void;
  onEdit: (data: any, isUserLevel: boolean) => void; 
  refreshTrigger: number;
}

type TableDataType = SchoolPermissionSummary | UserPermissionInfo;

const PermissionTable: React.FC<Props> = ({ 
  selectedNode, drillDownSchool, onDrillDown, onAdd, onEdit, refreshTrigger 
}) => {
  const { user } = useAuth();
  const [dataList, setDataList] = useState<TableDataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterMemo, setFilterMemo] = useState<string>('');
  const [searchUsername, setSearchUsername] = useState<string>('');

  // --- 1. 数据获取逻辑 (保持不变) ---
  useEffect(() => {
    if (!selectedNode) return;
    const fetchData = async () => {
      setLoading(true);
      setDataList([]); 
      try {
        const { type, id } = selectedNode;
        const schoolId = drillDownSchool?.id;
        
        if (schoolId) {
          // Level 2: User List
          let res: SchoolWithUsersPermission[] = [];
          if (type === 'course') res = (await getCourseUserPermissions(id, schoolId)).data;
          else if (type === 'stage') res = (await getStageUserPermissions(id, schoolId)).data;
          else if (type === 'lesson') res = (await getLessonUserPermissions(id, schoolId)).data;
          
          if (res && res.length > 0 && res[0].users) setDataList(res[0].users);
          else setDataList([]);
        } else {
          // Level 1: School Summary
          let res: SchoolPermissionSummary[] = [];
          if (type === 'course') res = (await api.getCourseSchoolPermissions(id)).data;
          else if (type === 'stage') res = (await api.getStageSchoolPermissions(id)).data;
          else if (type === 'lesson') res = (await api.getLessonSchoolPermissions(id)).data;
          setDataList(res);
        }
      } catch (e) { 
        console.error("Fetch failed", e); 
        setDataList([]);
      } finally { 
        setLoading(false); 
      }
    };
    fetchData();
  }, [selectedNode, drillDownSchool, refreshTrigger, localRefresh]);

  const isSchoolRow = (item: TableDataType): item is SchoolPermissionSummary => {
    return (item as SchoolPermissionSummary).schoolname !== undefined;
  };

  const availableRoles = useMemo(() => {
    const roles = new Set<string>();
    dataList.forEach(item => {
      if (!isSchoolRow(item) && item.role) {
        roles.add(item.role);
      }
    });
    return Array.from(roles).sort();
  }, [dataList]);

  const availableMemos = useMemo(() => {
    const memos = new Set<string>();
    dataList.forEach(item => {
      if (!isSchoolRow(item) && item.memo) {
        memos.add(item.memo);
      }
    });
    return Array.from(memos).sort();
  }, [dataList]);

  const filteredDataList = useMemo(() => {
    if (!drillDownSchool) return dataList;
    
    return dataList.filter(item => {
      if (isSchoolRow(item)) return true;
      
      const matchRole = !filterRole || item.role === filterRole;
      const matchMemo = !filterMemo || item.memo === filterMemo;
      const matchUsername = !searchUsername || 
        item.username.toLowerCase().includes(searchUsername.toLowerCase());
      
      return matchRole && matchMemo && matchUsername;
    });
  }, [dataList, drillDownSchool, filterRole, filterMemo, searchUsername]);

  // --- 2. 核心：机构层级批量删除逻辑 ---
  const handleDeleteSchoolAllUsers = async (schoolId: number, schoolName: string, userCount: number) => {
    if (!selectedNode) return;

    // 检查当前登录用户角色是否为sys
    if (user?.role !== 'sys') {
      alert("只有系统管理员(system)才能执行批量删除操作");
      return;
    }

    // B. 确认提示
    const confirmMsg = `⚠️ 高危操作警告 ⚠️\n\n您确定要移除机构 "${schoolName}" 下所有用户的权限吗？\n\n资源: ${selectedNode.type} - ${selectedNode.title}\n影响用户数: 约 ${userCount} 人\n\n此操作将通过循环调用 API 逐个移除用户权限。`;
    if (!window.confirm(confirmMsg)) return;

    setIsDeleting(true);
    try {
      const { type, id } = selectedNode;

      // C. 第一步：获取该机构下所有有权限的用户列表
      // 我们需要先知道要删谁，才能调用 delete...UserPermissions(id, userId)
      let usersToDelete: UserPermissionInfo[] = [];
      let fetchRes: SchoolWithUsersPermission[] = [];

      if (type === 'course') fetchRes = (await getCourseUserPermissions(id, schoolId)).data;
      else if (type === 'stage') fetchRes = (await getStageUserPermissions(id, schoolId)).data;
      else if (type === 'lesson') fetchRes = (await getLessonUserPermissions(id, schoolId)).data;
      
      if (fetchRes && fetchRes.length > 0 && fetchRes[0].users) {
        usersToDelete = fetchRes[0].users;
      }

      if (usersToDelete.length === 0) {
        alert("该机构下没有找到需要删除的用户权限。");
        setIsDeleting(false);
        return;
      }

      // D. 第二步：并发执行删除操作
      // 使用 Promise.all 并发调用提供的 User 级删除接口
      const deletePromises = usersToDelete.map(targetUser => {
        if (type === 'course') {
          return deleteCourseUserPermissions(id, targetUser.user_id);
        } else if (type === 'stage') {
          return deleteStageUserPermissions(id, targetUser.user_id);
        } else if (type === 'lesson') {
          return deleteLessonUserPermissions(id, targetUser.user_id);
        }
        return Promise.resolve();
      });

      await Promise.all(deletePromises);

      // E. 完成
      alert(`成功移除了 ${usersToDelete.length} 位用户的权限。`);
      setLocalRefresh(prev => prev + 1); // 刷新列表

    } catch (e) {
      console.error("Batch delete failed", e);
      alert("批量删除过程中发生错误，部分权限可能未移除。请刷新后重试。");
    } finally {
      setIsDeleting(false);
    }
  };

  // --- 3. 单个用户删除逻辑 (复用之前的) ---
  const handleDeleteSingleUser = async (targetUser: UserPermissionInfo) => {
    if (!selectedNode || !drillDownSchool) return;
    
    // 检查当前登录用户角色是否为sys
    if (user?.role !== 'sys') {
      alert("只有系统管理员(system)才能删除权限");
      return;
    }
    
    if (!window.confirm(`确定要移除用户 "${targetUser.username}" 的权限吗？`)) return;

    try {
      if (selectedNode.type === 'course') {
        await deleteCourseUserPermissions(selectedNode.id, targetUser.user_id);
      } else if (selectedNode.type === 'stage') {
        await deleteStageUserPermissions(selectedNode.id, targetUser.user_id);
      } else if (selectedNode.type === 'lesson') {
        await deleteLessonUserPermissions(selectedNode.id, targetUser.user_id);
      }
      setLocalRefresh(prev => prev + 1);
    } catch (e) { console.error(e); alert("删除失败"); }
  };

  if (!selectedNode) return <div className="flex-1 flex items-center justify-center text-gray-400">请选择左侧资源节点</div>;

  return (
    <>
      {/* 全局 Loading 遮罩 (删除时显示) */}
      {isDeleting && (
        <div className="absolute inset-0 bg-white/80 z-50 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-600 mb-4"></div>
          <p className="text-red-600 font-medium">正在批量移除权限，请勿关闭页面...</p>
        </div>
      )}

      {/* Header & Toolbar */}
      <div className="p-4 border-b bg-gray-50 flex justify-between items-center rounded-t-xl min-h-[70px]">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">当前资源上下文</div>
          <div className="font-bold text-gray-800 text-lg flex items-center gap-2 mt-1">
            {selectedNode.type === 'course' ? '📚' : selectedNode.type === 'stage' ? '🔹' : '📄'} 
            <span className="truncate max-w-[300px]" title={selectedNode.title}>{selectedNode.title}</span>
          </div>
        </div>
        {drillDownSchool && (
          <div className="flex items-center bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
            <span className="text-gray-500 text-sm mr-2">当前机构:</span>
            <span className="font-bold text-gray-700 mr-4">{drillDownSchool.schoolname}</span>
            <button 
              onClick={() => onDrillDown(null)} 
              className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 border border-blue-100"
            >
              ⬅ 返回一级列表
            </button>
          </div>
        )}
      </div>

      <div className="p-4 flex justify-between items-center bg-white border-b border-gray-100">
        <h3 className="font-medium text-gray-700 flex items-center gap-2">
          {drillDownSchool ? (
             <><span>👤 用户权限详情列表</span><span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full uppercase">Level 2</span></>
          ) : (
             <><span>🏫 已授权机构摘要列表</span><span className="text-[10px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full uppercase">Level 1</span></>
          )}
        </h3>
        <button 
          onClick={onAdd} 
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 shadow-sm flex items-center gap-1"
        >
          <span>+</span> 添加/修改权限
        </button>
      </div>

      {/* Filter Toolbar - Only show in Level 2 */}
      {drillDownSchool && (
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-3">
          <span className="text-sm text-gray-500 font-medium">筛选:</span>
          
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">全部角色</option>
            {availableRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <select
            value={filterMemo}
            onChange={(e) => setFilterMemo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white max-w-[200px]"
          >
            <option value="">全部备注</option>
            {availableMemos.map(memo => (
              <option key={memo} value={memo}>{memo}</option>
            ))}
          </select>

          <div className="relative flex-1 max-w-[250px]">
            <input
              type="text"
              placeholder="搜索用户名..."
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 pr-8"
            />
            {searchUsername && (
              <button
                onClick={() => setSearchUsername('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {(filterRole || filterMemo || searchUsername) && (
            <button
              onClick={() => {
                setFilterRole('');
                setFilterMemo('');
                setSearchUsername('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              清除筛选
            </button>
          )}

          <span className="text-xs text-gray-400 ml-auto">
            共 {filteredDataList.length} / {dataList.length} 条记录
          </span>
        </div>
      )}

      {/* Table Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50/50 relative">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
             <span className="text-sm">正在加载数据...</span>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                {drillDownSchool ? (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-20">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">用户</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">权限类型</th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">机构名称</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">授权人数</th>
                  </>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDataList.map((item) => {
                if (isSchoolRow(item)) {
                  // ================= LEVEL 1: SCHOOL ROW =================
                  return (
                    <tr key={`school-${item.school_id}`} className="hover:bg-blue-50/50 group">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">🏫</div>
                        {item.schoolname}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className="bg-gray-100 px-2.5 py-0.5 rounded-full text-xs font-bold border border-gray-200">
                          {item.user_count} 人
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm flex justify-end items-center gap-2">
                        {/* 1. 下钻按钮 */}
                        <button 
                          onClick={() => onDrillDown({ id: item.school_id, schoolname: item.schoolname } as School)} 
                          className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-md text-xs font-bold border border-blue-100"
                        >
                          管理用户 →
                        </button>
                        
                        {/* 2. 批量设置按钮 */}
                        <button 
                          onClick={() => onEdit({ school_id: item.school_id }, false)} 
                          className="text-indigo-500 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md text-xs font-medium border border-indigo-100"
                          title="批量修改全校权限类型"
                        >
                          批量修改
                        </button>

                        {/* 3. 🔥 新增：机构层级删除按钮 🔥 */}
                        <button
                          onClick={() => handleDeleteSchoolAllUsers(item.school_id, item.schoolname, item.user_count)}
                          className={`text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md text-xs font-medium border border-red-100 ${user?.role !== 'sys' ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={user?.role !== 'sys' ? '只有系统管理员才能执行批量删除' : '清空该机构下所有用户的权限'}
                          disabled={user?.role !== 'sys'}
                        >
                          全部移除
                        </button>
                      </td>
                    </tr>
                  );
                } else {
                  // ================= LEVEL 2: USER ROW =================
                  return (
                    <tr key={`user-${item.user_id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono">#{item.user_id}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-indigo-600 text-sm font-bold flex-shrink-0">
                            {item.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{item.username}</span>
                            <div className="flex items-center gap-2 mt-1">
                              <RoleBadge role={item.role} />
                              {item.memo && (
                                <span className="text-xs text-gray-500 truncate max-w-[200px]" title={item.memo}>
                                  📝 {item.memo}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PermBadge type={item.permission_type} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end items-center gap-3">
                        <button 
                          onClick={() => onEdit({ 
                            school_id: drillDownSchool!.id, 
                            user_id: item.user_id, 
                            permission_type: item.permission_type 
                          }, true)} 
                          className="text-indigo-600 hover:text-indigo-900 font-medium bg-indigo-50 px-3 py-1 rounded hover:bg-indigo-100 transition-colors"
                        >
                          修改
                        </button>

                        <button 
                          onClick={() => handleDeleteSingleUser(item)} 
                          className={`text-red-600 hover:text-red-800 font-medium bg-red-50 px-3 py-1 rounded hover:bg-red-100 transition-colors ${(selectedNode.type !== 'lesson' || user?.role !== 'sys') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={selectedNode.type !== 'lesson' || user?.role !== 'sys'}
                        >
                          删除
                        </button>
                      </td>
                    </tr>
                  );
                }
              })}
              
              {filteredDataList.length === 0 && (
                <tr>
                  <td colSpan={drillDownSchool ? 4 : 3} className="px-6 py-12 text-center text-gray-400 bg-white">
                    <p>{drillDownSchool ? '没有找到匹配的用户' : '暂无任何机构获得此资源授权'}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const PermBadge = ({ type }: { type: PermissionType }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${type === 'edit_develop' ? 'bg-purple-50 text-purple-700 border-purple-100' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>
    {type === 'edit_develop' ? '✏️ 编辑/开发' : '👁️ 仅查看'}
  </span>
);

const RoleBadge = ({ role }: { role: string }) => {
  const config: Record<string, { bg: string; text: string; label: string }> = {
    sys: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'SYS' },
    admin: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'ADMIN' },
    teacher: { bg: 'bg-green-100', text: 'text-green-700', label: 'TEACHER' },
    student: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'STUDENT' }
  };
  const { bg, text, label } = config[role] || config.student;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${bg} ${text}`}>
      {label}
    </span>
  );
};

export default PermissionTable;
